import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  IssueReason,
  ResolveAction,
  TransactionActivityLogEvent,
  TransactionItem,
  TransactionReportData,
} from "../../types";
import {
  EscalateIssueModal,
  FlagTransactionModal,
  ReconcileModal,
  ReportDetailsFlyin,
  ResolveIssueModal,
  TransactionActivityLogCard,
  TransactionAlertBanner,
  TransactionAttachmentsCard,
  TransactionDetailsHeader,
  TransactionOverviewCard,
} from "./";
import { downloadTransactionReceipt } from "../../services/downloadTransactionReceipt";
import { flagTransaction } from "../../services/flagTransaction";

export interface GeneralDetailsViewProps {
  transaction: TransactionItem;
  id?: string;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onNavigateSibling?: (delta: number) => void;
}

export const GeneralDetailsView: React.FC<GeneralDetailsViewProps> = ({
  transaction,
  id,
  canGoPrevious = false,
  canGoNext = false,
  onNavigateSibling,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isInitialFlagged = Boolean(
    transaction.isFlagged ||
      transaction.status?.toLowerCase() === "flagged"
  );

  const [isFlagged, setIsFlagged] = useState(isInitialFlagged);
  const [isResolved, setIsResolved] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(
    isInitialFlagged ? "Flagged" : transaction.status || "Completed"
  );

  const rawDate = transaction.transactionDate || transaction.createdAt || transaction.updatedAt;
  const transactionDateStr = rawDate
    ? new Date(rawDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "-";

  // Overlay visibility
  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);
  const [isReportFlyinOpen, setIsReportFlyinOpen] = useState(false);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [isEscalateModalOpen, setIsEscalateModalOpen] = useState(false);
  const [isReconcileModalOpen, setIsReconcileModalOpen] = useState(false);

  // Report & Timeline data
  const [reportData, setReportData] = useState<TransactionReportData | null>(null);

  // Synchronize flagged status whenever transaction prop updates
  useEffect(() => {
    const isNowFlagged = Boolean(
      transaction.isFlagged ||
        transaction.status?.toLowerCase() === "flagged"
    );
    if (isNowFlagged) {
      setIsFlagged(true);
      setCurrentStatus("Flagged");
    } else if (isResolved) {
      setIsFlagged(false);
      setCurrentStatus("Completed");
    } else {
      setIsFlagged(false);
      setCurrentStatus(transaction.status || "Completed");
    }
  }, [transaction.isFlagged, transaction.status, isResolved]);

  // Ensure reportData is populated if transaction is flagged
  useEffect(() => {
    const isNowFlagged = Boolean(
      transaction.isFlagged ||
        transaction.status?.toLowerCase() === "flagged"
    );
    if (isNowFlagged && !reportData) {
      const issueText =
        (transaction as any).issue ||
        (transaction as any).reason ||
        "Suspicious transaction activity";
      setReportData({
        reason: issueText,
        description: issueText,
        dateReported: transactionDateStr,
        reportedBy: "Admin",
        timeline: [
          {
            id: `tl-flag-${Date.now()}`,
            timestamp: transactionDateStr,
            title: `Transaction flagged (${issueText})`,
            actorName: "Admin",
            statusDotColor: "amber",
          },
        ],
      });
    }
  }, [transaction, reportData, transactionDateStr]);

  // Activity Log
  const [activityLogs, setActivityLogs] = useState<TransactionActivityLogEvent[]>([]);

  // Synchronize Activity Log from transaction statusHistory if present
  useEffect(() => {
    if (transaction.statusHistory && transaction.statusHistory.length > 0) {
      const historyLogs: TransactionActivityLogEvent[] = transaction.statusHistory.map(
        (sh, idx) => ({
          id: `sh-${idx}-${sh.timestamp}`,
          timestamp: sh.timestamp
            ? new Date(sh.timestamp).toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })
            : "Recently",
          actorName: "Admin",
          actionPrefix: sh.event || "Updated status to",
          boldTarget: sh.status || "Status",
          actionSuffix:
            sh.message && sh.message !== sh.status ? `(${sh.message})` : undefined,
        })
      );
      setActivityLogs((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const newItems = historyLogs.filter((h) => !existingIds.has(h.id));
        return [...prev, ...newItems];
      });
    } else {
      const initDate =
        transaction.transactionDate || transaction.createdAt || transaction.updatedAt;
      const formattedDate = initDate
        ? new Date(initDate).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })
        : "Recently";

      setActivityLogs((prev) => {
        if (prev.length > 0) return prev;
        return [
          {
            id: "act-init",
            timestamp: formattedDate,
            actorName:
              transaction.customer?.name || transaction.accountName || "Customer",
            actionPrefix: "Initiated",
            boldTarget: "Transaction",
            isCustomer: true,
          },
        ];
      });
    }
  }, [
    transaction.statusHistory,
    transaction.transactionDate,
    transaction.createdAt,
    transaction.updatedAt,
    transaction.customer?.name,
    transaction.accountName,
  ]);

  // Flag transaction handler
  const handleFlagSubmit = async (data: {
    issueReason: IssueReason | string;
    escalateType: "role" | "member";
    escalateTargetName: string;
    roleId?: string;
    adminId?: string;
    note: string;
  }) => {
    const targetId = transaction.id || transaction._id || id || "";
    setIsFlagged(true);
    setIsResolved(false);
    setCurrentStatus("Flagged");

    setReportData({
      reason: data.issueReason,
      description: data.note || "Transaction reported by admin for review.",
      dateReported: "May 11th, 2025",
      reportedBy: "Marie Curie",
      escalatedTo: { type: data.escalateType, name: data.escalateTargetName },
      timeline: [
        {
          id: `tl-${Date.now()}-1`,
          timestamp: "Just now",
          title: `Issue escalated to ${data.escalateTargetName}`,
          actorName: "Marie Curie",
          statusDotColor: "amber",
        },
        {
          id: `tl-${Date.now()}-2`,
          timestamp: "Just now",
          title: "Transaction reported",
          actorName: "Marie Curie",
          statusDotColor: "green",
        },
        {
          id: `tl-${Date.now()}-3`,
          timestamp: transactionDateStr,
          title: "Transaction initiated",
          actorName: transaction.customer?.name || "Customer",
          statusDotColor: "slate",
        },
      ],
    });

    setActivityLogs((prev) => [
      {
        id: `act-${Date.now()}`,
        timestamp: "Just now",
        actorName: "Marie Curie",
        actionPrefix: "Reported",
        boldTarget: `Transaction (${data.issueReason})`,
      },
      ...prev,
    ]);

    if (targetId) {
      try {
        await flagTransaction({
          transactionId: targetId,
          status: "Flagged",
          issue: String(data.issueReason),
          role: data.roleId || data.adminId || data.escalateTargetName,
        });

        // Invalidate react-query cache so all transaction views and lists refresh
        await queryClient.invalidateQueries({
          queryKey: ["admin-transaction-details"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["transactions"],
        });

        toast.success("Transaction flagged successfully");
      } catch (err: unknown) {
        console.warn("Flag transaction request failed:", err);
        const axiosErr = err as {
          response?: { data?: { message?: string | string[] } };
          message?: string;
        };
        const msg =
          axiosErr?.response?.data?.message ||
          axiosErr?.message ||
          "Failed to flag transaction";
        toast.error(Array.isArray(msg) ? msg.join(", ") : String(msg));
      }
    }
  };

  // Resolve issue handler
  const handleResolveSubmit = (data: { actionTaken: ResolveAction; note: string }) => {
    setIsResolved(true);
    setIsFlagged(false);
    setCurrentStatus("Completed");

    setReportData((prev) => ({
      reason: prev?.reason || "Transaction failed, but was debited",
      description: prev?.description || "Issue investigated and resolved.",
      dateReported: prev?.dateReported || "Recently",
      reportedBy: prev?.reportedBy || "Customer",
      resolution: {
        actionTaken: data.actionTaken,
        resolvedBy: "Admin",
        resolvedAt: "Just now",
        resolutionTimeText: "recently",
        note: data.note,
      },
      timeline: [
        {
          id: `tl-res-${Date.now()}`,
          timestamp: "Just now",
          title: "Issue resolved",
          actorName: "Admin",
          statusDotColor: "green",
        },
        {
          id: `tl-adj-${Date.now()}`,
          timestamp: "Just now",
          title: "Wallet adjusted",
          actorName: "Admin",
          statusDotColor: "slate",
        },
        ...(prev?.timeline || []),
      ],
    }));

    setActivityLogs((prev) => [
      {
        id: `act-res-${Date.now()}`,
        timestamp: "Just now",
        actorName: "Admin",
        actionPrefix: "Marked",
        boldTarget: `Transaction as resolved (${data.actionTaken})`,
      },
      ...prev,
    ]);

    toast.success("Issue successfully marked as resolved");
  };

  // Escalate issue handler
  const handleEscalateSubmit = (data: {
    escalateType: "role" | "member";
    targetName: string;
    note: string;
  }) => {
    setReportData((prev) => ({
      reason: prev?.reason || "Transaction issue",
      description: prev?.description || "Escalated for further action",
      dateReported: prev?.dateReported || "May 11th, 2025",
      reportedBy: prev?.reportedBy || "Customer",
      timeline: [
        {
          id: `tl-esc-${Date.now()}`,
          timestamp: "Just now",
          title: `Issue escalated to ${data.targetName}`,
          actorName: "Marie Curie",
          actorRole: data.escalateType === "role" ? "Role" : "Admin",
          statusDotColor: "amber",
        },
        ...(prev?.timeline || []),
      ],
    }));

    setActivityLogs((prev) => [
      {
        id: `act-esc-${Date.now()}`,
        timestamp: "Just now",
        actorName: "Marie Curie",
        actionPrefix: "Escalated",
        boldTarget: `Issue to ${data.targetName}`,
      },
      ...prev,
    ]);

    toast.success(`Issue escalated to ${data.targetName}`);
  };

  // Reconcile handler
  const handleReconcileConfirm = (data: { reason: string; adjustWallet: boolean }) => {
    setCurrentStatus("Completed");
    setIsReconcileModalOpen(false);

    setActivityLogs((prev) => [
      {
        id: `act-rec-${Date.now()}`,
        timestamp: "Just now",
        actorName: "Marie Curie",
        actionPrefix: "Reconciled",
        boldTarget: "Transaction from Pending to Completed",
        actionSuffix: data.adjustWallet ? "with wallet ledger adjustment" : "",
      },
      ...prev,
    ]);

    toast.success("Transaction reconciled and wallet balance updated");
  };

  const handleDownloadReceipt = () => {
    const targetTrxId = id || transaction._id || transaction.id;
    if (targetTrxId) {
      downloadTransactionReceipt(targetTrxId);
    } else if (transaction.url?.url) {
      window.open(transaction.url.url, "_blank");
    } else {
      navigate("/receipt/home", { state: transaction });
    }
  };

  return (
    <section className="min-h-full bg-[#F3F4F8] md:p-6">
      <div className="rounded-[16px] bg-white p-4 md:p-6">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
        <TransactionDetailsHeader
          transaction={transaction}
          status={currentStatus}
          isFlagged={isFlagged}
          canGoPrevious={canGoPrevious}
          canGoNext={canGoNext}
          onNavigateSibling={onNavigateSibling}
          onReconcile={() => setIsReconcileModalOpen(true)}
          onDownloadReceipt={handleDownloadReceipt}
          onFlagTransaction={() => setIsFlagModalOpen(true)}
        />

        <TransactionAlertBanner
          isFlagged={isFlagged}
          isResolved={isResolved}
          reportData={reportData}
          onReviewReport={() => setIsReportFlyinOpen(true)}
        />

        <TransactionOverviewCard
          transaction={transaction}
          status={currentStatus}
          isFlagged={isFlagged}
          onRefreshStatus={() => toast.info("Status is up to date")}
          onViewWallet={() => toast.info("Opening customer wallet ledger")}
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="flex flex-col gap-3">
            <h2 className="text-[16px] font-semibold text-[#101828]">Attachments</h2>
            <TransactionAttachmentsCard transaction={transaction} />
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-[16px] font-semibold text-[#101828]">Activity Log</h2>
            <TransactionActivityLogCard events={activityLogs} />
          </div>
        </div>
      </div>
    </div>

      <FlagTransactionModal
        open={isFlagModalOpen}
        onOpenChange={setIsFlagModalOpen}
        onSubmit={handleFlagSubmit}
      />

      <ReportDetailsFlyin
        open={isReportFlyinOpen}
        onOpenChange={setIsReportFlyinOpen}
        reportData={reportData}
        activityLogs={activityLogs}
      />

      <ResolveIssueModal
        open={isResolveModalOpen}
        onOpenChange={setIsResolveModalOpen}
        onSubmit={handleResolveSubmit}
      />

      <EscalateIssueModal
        open={isEscalateModalOpen}
        onOpenChange={setIsEscalateModalOpen}
        onSubmit={handleEscalateSubmit}
      />

      <ReconcileModal
        open={isReconcileModalOpen}
        transaction={transaction}
        onOpenChange={setIsReconcileModalOpen}
        onConfirm={handleReconcileConfirm}
      />
    </section>
  );
};
